import {Button, Divider, Stack, Typography} from '@mui/material';
import {Provider} from 'domain/usecases/private/authorizations/get-providers-list';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {AutoSolEnum} from 'main/analytics/gaTagsEnum';
import {EmptySearchState, ErrorState, MainHeader, SentinelLoading} from 'presentation/components';
import {Drawer} from 'presentation/components/Drawer';
import {Filter} from 'presentation/components/Filter';
import {LIST_SIZE} from 'presentation/pages/private/authorizations/constants';
import {useRequestAuthorization} from 'presentation/pages/private/authorizations/hooks';
import {useSearchProviders} from 'presentation/pages/private/authorizations/hooks/useSearchProviders';
import {getProvidersList} from 'presentation/pages/private/authorizations/services/getProvidersList';
import {useAuthorizationsStore} from 'presentation/pages/private/authorizations/store/authorizationsStore';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useGetPaginatedItemsReactQuery} from 'presentation/pages/private/hooks/useGetPaginatedItemsReactQuery';
import {translations} from 'presentation/translations-new';
import React, {Fragment} from 'react';
import {LoadingState} from '../LoadingState';
import {ProviderList} from '../ProviderList';
import {ProviderItem} from './SelectedProviderCard';
import {DrawerContentContainer, SearchBarContainer} from './styles';
import {ProviderDrawerContentProps} from './types';

const {confirm, failToLoadTitle, failToLoadDescription, noResults, clearSearch} =
	translations['pt-br'].commons;
const {
	emptySearch: {noFranchisesFoundBySearchDescription},
} = translations['pt-br'].privateFlow.franchisesTable;
const {
	providerDrawer: {title, searchPlaceholder, addedProviders},
} = translations['pt-br'].privateFlow.requestAuthorizations.request.ProviderSelection;

export const DrawerContent = ({
	onCloseDrawer,
	selectedProviderList,
	handleToggleProvider,
}: ProviderDrawerContentProps) => {
	const {isMobile} = useMediaQueries();
	const setProvidersList = useAuthorizationsStore(state => state.setProvidersList);

	const {cdUfSelected, nmCidadeSelected} = useRequestAuthorization();

	const {searchValueState, debouncedQueryFunc, clearSearchParams, abortDebounce} =
		useSearchProviders();

	const {items, loadingItemsList, itemsListHasError, itemsListIsEmpty, loadMore, refresh} =
		useGetPaginatedItemsReactQuery<Provider>({
			queryKey: [
				QUERY_KEYS.AUTOSOL_REQUEST_PROVIDERS_LIST,
				searchValueState,
				cdUfSelected,
				nmCidadeSelected,
			],
			queryFn: async ({page}) => {
				const response = await getProvidersList({
					cdUf: cdUfSelected,
					nmCidade: nmCidadeSelected,
					page,
					size: LIST_SIZE,
					search: searchValueState.toUpperCase() || undefined,
				});

				return {
					items: response.content,
					pages: Math.ceil(response.totalElements / LIST_SIZE),
					total: response.totalElements,
				};
			},
		});

	const shouldShowSelectedProvidersList =
		selectedProviderList.length > 0 && items.length > 0 && !itemsListHasError;

	return (
		<Fragment>
			{isMobile ? (
				<MainHeader
					title={title}
					dataTagId={{closeButton: AutoSolEnum.CLICK_FECHAR_DRAWER_PRESTADORES}}
					handleBack={onCloseDrawer}
					handleClose={onCloseDrawer}
					variant="subRoute"
				/>
			) : (
				<Drawer.Header
					closeBtnDataTagId={AutoSolEnum.CLICK_FECHAR_DRAWER_PRESTADORES}
					onClose={onCloseDrawer}
					title={title}
				/>
			)}

			<DrawerContentContainer>
				<SearchBarContainer>
					<Filter.Search
						handleChange={() => {
							abortDebounce();
							debouncedQueryFunc();
						}}
						clearSearch={clearSearchParams}
						placeholder={searchPlaceholder}
						value={searchValueState}
					/>
					{shouldShowSelectedProvidersList ? (
						<Stack gap={1}>
							<Typography variant="body2" fontWeight={600} color="text.secondary">
								{addedProviders} {`(${selectedProviderList.length}/3)`}
							</Typography>
							{selectedProviderList.map(provider => (
								<ProviderItem
									key={provider.idUnico}
									provider={provider}
									handleToggleProvider={handleToggleProvider}
								/>
							))}
							{!isMobile ? <Divider sx={{mt: 2}} /> : null}
						</Stack>
					) : null}
				</SearchBarContainer>

				{items.length > 0 ? (
					<Stack mb={isMobile ? '78px' : 0}>
						<ProviderList
							providers={items}
							selectedIds={selectedProviderList.map(p => p.idUnico)}
							onChange={handleToggleProvider}
						/>

						<SentinelLoading
							minHeight="200px"
							onVisible={() => {
								if (items.length > 0) {
									loadMore();
								}
							}}
							isLoading={loadingItemsList}
							showLoadingText={false}
						/>
					</Stack>
				) : null}

				{items.length === 0 && loadingItemsList ? (
					<Stack gap={2} px={2} pt={isMobile ? 2 : 0}>
						<LoadingState />
						<LoadingState />
					</Stack>
				) : null}

				{itemsListHasError ? (
					<ErrorState
						title={failToLoadTitle}
						description={failToLoadDescription}
						handleRefresh={refresh}
					/>
				) : null}

				{itemsListIsEmpty && !itemsListHasError ? (
					<Stack p={isMobile ? 2 : 0} sx={{height: '100%'}}>
						<EmptySearchState
							title={noResults}
							description={noFranchisesFoundBySearchDescription}
							customFlexDirection="column"
							customMaxWidthContent="100%"
							customWidthContent="100%"
							customMargin="0px"
							sx={{height: '100%', padding: 2}}>
							<Button
								variant="outlined"
								size="medium"
								color="primary"
								onClick={clearSearchParams}>
								{clearSearch}
							</Button>
						</EmptySearchState>
					</Stack>
				) : null}
			</DrawerContentContainer>

			{!itemsListHasError && !itemsListIsEmpty ? (
				<Drawer.Footer
					secondButtonTitle={confirm}
					secondButtonDataTagId={AutoSolEnum.CLICK_CONTINUAR_BUSCA_PRESTADORES}
					secondButtonOnClick={() => {
						setProvidersList(selectedProviderList);
						onCloseDrawer();
					}}
					hideFirstButton
				/>
			) : null}
		</Fragment>
	);
};
